import * as React from 'react';
import {
  useController,
  useFormState,
  type Control,
  type ControllerFieldState,
  type ControllerRenderProps,
  type FieldPath,
  type FieldValues,
} from 'react-hook-form';

import { FormField } from '../patterns/FormField';

import { rhfInlineError } from './rhfInlineError';
import type { RhfLayoutProps } from './rhfLayout';

export interface RhfFieldProps extends RhfLayoutProps {
  control: Control<FieldValues>;
  name: string;
  label: string;
  hint?: string;
  required?: boolean;
  orientation?: 'vertical' | 'horizontal';
  semanticId?: string;
  disabled?: boolean;
  children: (args: {
    field: ControllerRenderProps<FieldValues, FieldPath<FieldValues>>;
    fieldState: ControllerFieldState;
  }) => React.ReactNode;
}

export const RhfField = React.forwardRef<HTMLDivElement, RhfFieldProps>(function RhfField(
  {
    control,
    name,
    label,
    hint,
    required = false,
    orientation = 'vertical',
    semanticId,
    disabled = false,
    controlWidth = 'auto',
    children,
  },
  ref,
) {
  const { field, fieldState } = useController({ control, name: name as FieldPath<FieldValues> });
  const { isSubmitted } = useFormState({ control });

  const errorText = rhfInlineError(fieldState, { isSubmitted });

  return (
    <FormField
      ref={ref}
      label={label}
      hint={hint}
      required={required}
      orientation={orientation}
      semanticId={semanticId}
      disabled={disabled}
      error={errorText}
      controlWidth={controlWidth}
    >
      {children({ field, fieldState })}
    </FormField>
  );
});

RhfField.displayName = 'RhfField';
